/*
 * Chip (Figma 119:13967) — "Removable selected filter. Small fits inside select fields; Medium is
 * used in the active-filter bar."
 * | Size   | Label wrap                         | Remove wrap                  | Remove icon |
 * | small  | padding 2px 0 2px 8px              | padding 6px                  | 12px        |
 * | medium | padding 6px 0 6px 8px              | padding 8px, width 32px      | 16px        |
 * Both: --accent-weak fill, radius 6 (left on label, right on remove); label 400 14/20,
 * tracking 0.14px (1%), --accent-default.
 * Not in the design: a label longer than the space (a phone) ends in an ellipsis, with the whole
 * label as its tooltip; on a touch screen the remove button takes taps 40px around (unseen).
 * Motion (not in the design): a chip pops in where it appears (a filter turned on); its remove
 * button darkens and gives way on press.
 */
import { cx } from './cx'
import { Icon } from './Icon'
import { xCircle12, xCircle16 } from './icons'

export function Chip({
    label,
    size = 'small',
    onRemove,
    removeLabel,
}: {
    label: string
    size?: 'small' | 'medium'
    onRemove?: () => void
    /** Accessible name of the remove button, e.g. "Eemalda filter Prioriteet: Kõrge". */
    removeLabel?: string
}) {
    const medium = size === 'medium'
    return (
        <span className="motion-pop inline-flex max-w-full shrink-0 items-stretch rounded-[6px]">
            <span
                className={cx(
                    'flex min-w-0 items-center overflow-clip bg-[var(--accent-weak)] pl-[8px]',
                    medium ? 'py-[6px]' : 'py-[2px]',
                    onRemove ? 'rounded-l-[6px]' : 'rounded-[6px] pr-[8px]',
                )}
            >
                <span title={label} className="overflow-hidden text-ellipsis whitespace-nowrap text-[14px] leading-[20px] font-normal tracking-[0.14px] text-[var(--accent-default)]">
                    {label}
                </span>
            </span>
            {onRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label={removeLabel ?? label}
                    className={cx(
                        'press relative flex shrink-0 cursor-pointer items-center justify-center overflow-clip rounded-r-[6px] bg-[var(--accent-weak)] hover:bg-[color-mix(in_srgb,var(--accent-weak),var(--accent-default)_8%)] active:bg-[color-mix(in_srgb,var(--accent-weak),var(--accent-default)_16%)]',
                        // Touch: a 40px tap area around the 32px (medium) or 24px (small) button, not drawn.
                        "pointer-coarse:overflow-visible pointer-coarse:before:absolute pointer-coarse:before:content-['']",
                        medium ? 'pointer-coarse:before:-inset-[4px]' : 'pointer-coarse:before:-inset-[8px]',
                        'focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none',
                        medium ? 'w-[32px] p-[8px]' : 'p-[6px]',
                    )}
                >
                    <Icon src={medium ? xCircle16 : xCircle12} size={medium ? 16 : 12} />
                </button>
            )}
        </span>
    )
}
